import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Loader2, Plus, X } from "lucide-react";
import { api, peso } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BuyerNeedCard } from "@/components/BuyerNeedCard";
import { LocationFilter } from "@/pages/Marketplace";
import { PROPERTY_TYPE_GROUPS, propertyTypeLabel } from "@/lib/taxonomy";

const ANY = "__any__";

const PANEL_IMG = `${process.env.PUBLIC_URL || ""}/buyer-needs/bn-panel.webp`;

/**
 * BuyerNeedsBrowser — the dedicated network Buyer Needs discovery surface.
 * Reuses the SAME data (/public/buyer-needs, server-enforced visibility + expiry,
 * including excluding past-dated Stays), the SAME BuyerNeedCard, and the SAME posting
 * access. No duplicate data store or workflow. Professionals only (server-enforced).
 */
export function BuyerNeedsBrowser({ hidePostHeader = false, panelBackdrop = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isPro = user && (user.account_type || "PROFESSIONAL") !== "CONSUMER";
  const verifiedPro = isPro && user?.verification_status === "PROFESSIONAL_VERIFIED";

  const [loc, setLoc] = useState({ code: "", name: "" });
  const [propType, setPropType] = useState(ANY);
  const [listingType, setListingType] = useState(ANY);
  const [minBudget, setMinBudget] = useState("");
  const [maxBudget, setMaxBudget] = useState("");
  const [bedrooms, setBedrooms] = useState(ANY);
  const [guests, setGuests] = useState(ANY);
  const [minNightly, setMinNightly] = useState("");
  const [maxNightly, setMaxNightly] = useState("");
  const [minBeds, setMinBeds] = useState(ANY);
  const isStay = listingType === "Unit Hosting";

  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("newest");
  const pageRef = useRef(1);

  const buildParams = () => {
    const params = {};
    if (loc.code) params.location_code = loc.code; else if (loc.name) params.city = loc.name;
    if (propType !== ANY) params.property_type = propType;
    if (listingType !== ANY) params.listing_type = listingType;
    if (isStay) {
      if (minNightly) params.min_nightly = Number(minNightly);
      if (maxNightly) params.max_nightly = Number(maxNightly);
      if (guests !== ANY) params.guests = Number(guests);
      if (minBeds !== ANY) params.min_beds = Number(minBeds);
    } else {
      if (minBudget) params.min_budget = Number(minBudget);
      if (maxBudget) params.max_budget = Number(maxBudget);
      if (bedrooms !== ANY) params.bedrooms = Number(bedrooms);
    }
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/buyer-needs", { params: { ...buildParams(), page: 1, page_size: 24 } });
      pageRef.current = 1;
      setItems(data.items || []);
      setTotal(data.total ?? (data.items || []).length);
      setHasMore(!!data.has_more);
    } catch { setItems([]); setTotal(0); setHasMore(false); } finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const { data } = await api.get("/public/buyer-needs", { params: { ...buildParams(), page: nextPage, page_size: 24 } });
      pageRef.current = nextPage;
      setItems((prev) => [...(prev || []), ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch { /* keep list */ } finally { setLoadingMore(false); }
  };

  useEffect(() => { if (items === null) load(); /* eslint-disable-next-line */ }, []);

  const reload = () => load();
  const chips = [];
  if (loc.code || loc.name) chips.push({ k: "loc", label: loc.name || "Location", onRemove: () => { setLoc({ code: "", name: "" }); reload(); } });
  if (listingType !== ANY) chips.push({ k: "txn", label: isStay ? "Stays" : listingType, onRemove: () => { setListingType(ANY); reload(); } });
  if (propType !== ANY) chips.push({ k: "ptype", label: propertyTypeLabel(propType), onRemove: () => { setPropType(ANY); reload(); } });
  if (minBudget || maxBudget) chips.push({ k: "budget", label: `${minBudget ? peso(minBudget) : "Any"}–${maxBudget ? peso(maxBudget) : "Any"}`, onRemove: () => { setMinBudget(""); setMaxBudget(""); reload(); } });
  if (minNightly || maxNightly) chips.push({ k: "nightly", label: `${minNightly ? peso(minNightly) : "Any"}–${maxNightly ? peso(maxNightly) : "Any"} /night`, onRemove: () => { setMinNightly(""); setMaxNightly(""); reload(); } });
  if (bedrooms !== ANY) chips.push({ k: "beds", label: `${bedrooms}+ beds`, onRemove: () => { setBedrooms(ANY); reload(); } });
  if (guests !== ANY) chips.push({ k: "guests", label: `${guests}+ guests`, onRemove: () => { setGuests(ANY); reload(); } });
  if (minBeds !== ANY) chips.push({ k: "minbeds", label: `${minBeds}+ beds`, onRemove: () => { setMinBeds(ANY); reload(); } });
  const clearAll = () => {
    setLoc({ code: "", name: "" }); setPropType(ANY); setListingType(ANY);
    setMinBudget(""); setMaxBudget(""); setBedrooms(ANY); setGuests(ANY);
    setMinNightly(""); setMaxNightly(""); setMinBeds(ANY);
    setTimeout(load, 0);
  };

  const sorted = items ? [...items].sort((a, b) => {
    if (sort === "budget_desc") return (b.budget_max || b.budget_min || 0) - (a.budget_max || a.budget_min || 0);
    if (sort === "updated") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    return new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0);
  }) : [];

  if (!isPro) {
    return (
      <div>
        <div data-testid="bn-eligibility-note" className="rounded-xl border border-[#BBD4F0] bg-[#EDF3FA] px-4 py-3 mb-5 text-sm text-[#17365D] flex items-start gap-2">
          <Users className="h-4 w-4 mt-0.5 shrink-0 text-[#C6A04B]" />
          <span>You can browse active buyer needs. Posting a buyer need or responding with a matching property is available to verified real estate professionals.</span>
        </div>
        {renderBrowser()}
      </div>
    );
  }
  return renderBrowser();

  function renderBrowser() {
   return (
    <div>
      {/* Filters */}
      <div className="relative overflow-hidden bg-white rounded-2xl shadow-sm border border-[#DCE5EF] p-4">
        {panelBackdrop && (
          <>
            <img src={PANEL_IMG} alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 bottom-0 w-full h-full object-cover object-bottom opacity-60" />
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/55 via-white/80 to-white" />
          </>
        )}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div className="lg:col-span-2">
            <label className="text-xs font-semibold text-slate-500">Location</label>
            <div className="mt-1.5"><LocationFilter value={loc} onPick={setLoc} /></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Transaction</label>
            <Select value={listingType} onValueChange={setListingType}>
              <SelectTrigger data-testid="bn-listing-type" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any</SelectItem><SelectItem value="For Sale">For Sale</SelectItem><SelectItem value="For Rent">For Rent</SelectItem><SelectItem value="Unit Hosting">Stays</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Property type</label>
            <Select value={propType} onValueChange={setPropType}>
              <SelectTrigger data-testid="bn-type-select" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent><SelectItem value={ANY}>Any type</SelectItem>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent>
            </Select>
          </div>
          {isStay ? (
            <>
              <div><label className="text-xs font-semibold text-slate-500">Min nightly (₱)</label><CurrencyInput data-testid="bn-min-nightly" value={minNightly} onValueChange={setMinNightly} placeholder="0" className="mt-1.5" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Max nightly (₱)</label><CurrencyInput data-testid="bn-max-nightly" value={maxNightly} onValueChange={setMaxNightly} placeholder="Any" className="mt-1.5" /></div>
            </>
          ) : (
            <>
              <div><label className="text-xs font-semibold text-slate-500">Min budget (₱)</label><CurrencyInput data-testid="bn-min-budget" value={minBudget} onValueChange={setMinBudget} placeholder="0" className="mt-1.5" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Max budget (₱)</label><CurrencyInput data-testid="bn-max-budget" value={maxBudget} onValueChange={setMaxBudget} placeholder="Any" className="mt-1.5" /></div>
            </>
          )}
        </div>
        <div className="relative z-10 flex flex-wrap items-end justify-between gap-3 mt-3">
          {isStay ? (
            <div className="flex flex-wrap gap-3">
              <div className="w-40"><label className="text-xs font-semibold text-slate-500">Guests</label>
                <Select value={guests} onValueChange={setGuests}><SelectTrigger data-testid="bn-guests" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4", "5", "6", "8"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent></Select></div>
              <div className="w-40"><label className="text-xs font-semibold text-slate-500">Min beds</label>
                <Select value={minBeds} onValueChange={setMinBeds}><SelectTrigger data-testid="bn-min-beds" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent></Select></div>
            </div>
          ) : (
            <div className="w-40"><label className="text-xs font-semibold text-slate-500">Min bedrooms</label>
              <Select value={bedrooms} onValueChange={setBedrooms}><SelectTrigger data-testid="bn-bedrooms" className="mt-1.5 h-11"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value={ANY}>Any</SelectItem>{["1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t}+</SelectItem>)}</SelectContent></Select></div>
          )}
          <Button data-testid="bn-apply" onClick={load} className="h-11 px-8 bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white rounded-xl font-semibold">Search Buyer Needs</Button>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="bn-active-filters">
          {chips.map((c) => (
            <button key={c.k} data-testid={`bn-chip-${c.k}`} onClick={c.onRemove} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 pl-3 pr-2 py-1 text-sm text-slate-700 hover:border-[#0F2C59] transition-colors max-w-[240px]">
              <span className="truncate">{c.label}</span><X className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            </button>
          ))}
          {chips.length > 1 && <button data-testid="bn-clear-all" onClick={clearAll} className="text-sm font-semibold text-[#0F2C59] hover:text-[#C79A3B] transition-colors ml-1">Clear all</button>}
        </div>
      )}

      <div className="py-8">
        {loading || items === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="buyer-needs-empty" className="text-center py-20">
            <Users className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3 text-sm">No public Buyer Needs match your filters yet.</p>
            {verifiedPro && <Button data-testid="bn-post-empty" onClick={() => nav("/have-buyer")} className="mt-5 h-10 rounded-xl bg-[#C79A3B] hover:bg-[#C79A3B]/90 text-white font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Buyer Need</Button>}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <p className="text-sm text-slate-500" data-testid="bn-result-count">{total} buyer need{total === 1 ? "" : "s"} looking for a match</p>
              <div className="flex items-center gap-2 shrink-0">
                {verifiedPro && !hidePostHeader && <Button data-testid="bn-post" onClick={() => nav("/have-buyer")} variant="outline" className="h-9 rounded-xl border-[#0F2C59] text-[#0F2C59] font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Post Buyer Need</Button>}
                <span className="text-xs text-slate-400 hidden sm:inline">Sort by</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="bn-sort" className="h-9 w-[170px] text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="updated">Recently Updated</SelectItem><SelectItem value="budget_desc">Budget: High to Low</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="buyer-needs-grid">
              {sorted.map((n) => <BuyerNeedCard key={n.id} n={n} />)}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button data-testid="bn-load-more" onClick={loadMore} disabled={loadingMore} variant="outline" className="px-8 py-2.5 rounded-xl border-[#0F2C59]/20 text-[#0F2C59] font-semibold hover:bg-[#0F2C59]/5">
                  {loadingMore ? "Loading…" : `Load more (${items.length} of ${total})`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
  }
}

export default BuyerNeedsBrowser;
